import type {
  EstablishedAhpInitialSubmissionCoordinator,
  InitialAhpSubmissionRequest,
} from '@github-ui/ahp-session-client/initial-submission'
import {
  preparedPromptPayloadChars,
  type AhpChatStartRequest,
  type EstablishedAhpSession,
} from '@github-ui/ahp-session-client/contracts'
import {createHandoffStorage, type HandoffFailure} from '@github-ui/ahp-session-client/handoff-storage'
import {getCurrentUserLogin} from '@github-ui/client-env'

import {SubmissionAbandonedError, UserFacingSubmissionError} from '@github-ui/ahp-session-client/submission-error'

/**
 * A composed-but-undispatched first turn, handed from a composer on one page to
 * the chat surface on another. It stores the same `PreparedPrompt` the session
 * client would have received in place — text, attachments (images and documents
 * alike) and model — so the receiving surface replays the turn the user composed
 * rather than a reconstruction of it.
 *
 * `metadata` carries the composer's submission metadata (the selected space), so
 * the replayed turn lands in the same place the user aimed it.
 */
export interface ComposerHandoff extends AhpChatStartRequest {
  /**
   * The account that composed the turn. `sessionStorage` is tab-scoped but not
   * account-scoped — it survives a sign-out and sign-in in the same tab — so a
   * record left unclaimed could otherwise be replayed by whoever signs in next,
   * submitting the previous user's prompt and attachment contents.
   */
  owner?: string
  /**
   * Identifies the submission that stored this turn. The key is shared by every
   * dashboard page in the tab, and several can sit in the back-forward cache at
   * once, so a suspended page cannot assume the record it finds on resume is
   * still its own — a newer submission may have replaced it.
   */
  id?: string
}

const HANDOFF_KEY = 'copilot-chat-composer-handoff'

/**
 * Sums the turn's payload without copying it. Prompt text counts alongside
 * attachments: the composer sets no `maxLength`, so a pasted multi-megabyte
 * prompt is just as expensive to serialize as a file of the same size.
 */
function handoffPayloadChars(handoff: ComposerHandoff): number {
  return preparedPromptPayloadChars(handoff.prompt)
}

/**
 * Both handoffs share one ceiling, sized from the `sessionStorage` quota — see
 * `DEFAULT_MAX_HANDOFF_PAYLOAD_CHARS`. The composer accepts images with no size
 * limit and documents up to 32 MB, which base64 inflates to roughly 43 MB, so a
 * turn can genuinely be too large to carry across a navigation.
 *
 * An oversized turn is reported rather than trimmed. Shedding attachments to fit
 * would hand off a turn missing content the user attached and believes they
 * sent, so the caller explains the refusal instead.
 */
const composerHandoffStorage = createHandoffStorage<ComposerHandoff>({
  key: HANDOFF_KEY,
  payloadChars: handoffPayloadChars,
  getId: handoff => handoff.id,
})

export function storeComposerHandoff(handoff: ComposerHandoff): HandoffFailure | null {
  return composerHandoffStorage.write({...handoff, owner: getCurrentUserLogin()})
}

/**
 * A turn is replayable when it carries something to send. An image-only turn is
 * valid (the session input accepts an empty text when attachments are present),
 * so requiring prompt text here would strand one after the composer had already
 * stored it and navigated away.
 */
function isReplayable(handoff: ComposerHandoff): boolean {
  const prompt = handoff?.prompt
  if (!prompt || typeof prompt.text !== 'string') return false
  return prompt.text.trim().length > 0 || (prompt.attachments?.length ?? 0) > 0
}

export function readComposerHandoff(): ComposerHandoff | null {
  const parsed = composerHandoffStorage.read()
  if (!parsed || !isReplayable(parsed)) return null
  // A record composed by a different account is discarded rather than replayed:
  // the tab may have been signed out and back in as someone else since.
  if (parsed.owner !== getCurrentUserLogin()) {
    clearComposerHandoff()
    return null
  }
  return parsed
}

/**
 * The id of the record still sitting in storage, or `null` if none is there.
 *
 * An identity check, not a read, which is why it goes to the raw record instead
 * of `readComposerHandoff`. That answers "is there a turn worth replaying" and
 * the callers here ask "is the record in storage still the one I stored" — a
 * different question, and one the validation would answer wrongly. A record that
 * is unreadable or belongs to another account reads as absent there, which would
 * have a caller conclude its own turn was already claimed and leave it behind
 * for a later visit to replay; the account check also clears the record as a
 * side effect, which a question has no business doing.
 *
 * It does parse, since the id lives in the payload. That cost is only paid on
 * the withdrawal paths, which run once per submission.
 */
export function storedComposerHandoffId(): string | null {
  return composerHandoffStorage.storedId()
}

/**
 * Claims the stored turn, reporting whether it is gone. `removeItem` can throw
 * independently of acquiring the store, and the caller must not treat a turn it
 * failed to remove as claimed — replaying one that is still stored would submit
 * it again on the next visit.
 */
export function clearComposerHandoff(): boolean {
  return composerHandoffStorage.clear()
}

/**
 * Shown when a composed turn is too large to survive the navigation. Naming the
 * fix matters because it isn't guessable, and it covers both causes: the limit
 * counts prompt text as well as attachments.
 */
export const HANDOFF_TOO_LARGE_MESSAGE =
  'This message is too large to open in Copilot Chat. Try shortening it or removing an attachment, then send again.'

/**
 * Shown when the browser's storage is full. Deliberately does not mention
 * attachments: this turn may have none, and the space was taken by something
 * else, so the actionable advice is to free storage rather than trim the message.
 */
export const HANDOFF_STORAGE_FULL_MESSAGE =
  'Copilot Chat could not be opened because browser storage is full. Free up browser storage and try again.'

/**
 * Shown when the browser denies storage entirely (blocked cookies, sandboxed
 * frame). Trimming the message would not help here, so the advice differs.
 */
export const HANDOFF_UNAVAILABLE_MESSAGE =
  'Copilot Chat could not be opened because browser storage is unavailable. Check your browser settings and try again.'

const HANDOFF_FAILURE_MESSAGES: Record<HandoffFailure, string> = {
  'too-large': HANDOFF_TOO_LARGE_MESSAGE,
  'storage-full': HANDOFF_STORAGE_FULL_MESSAGE,
  unavailable: HANDOFF_UNAVAILABLE_MESSAGE,
}

export class ComposerHandoffError extends UserFacingSubmissionError {
  readonly failure: HandoffFailure

  constructor(failure: HandoffFailure) {
    super(HANDOFF_FAILURE_MESSAGES[failure])
    this.name = 'ComposerHandoffError'
    this.failure = failure
  }
}

/**
 * An initial-submission coordinator that composes but never establishes a
 * session: it captures the prepared first turn and hands it to another surface.
 * Used by entry-point composers (the dashboard) so the turn the user composed —
 * attachments, model and space included — is the turn immersive chat receives,
 * without provisioning a session behind the entry point.
 *
 * `submit` never resolves on success. The host navigates away in response to
 * `onHandoff`, and leaving the promise pending keeps the composer in its
 * submitting state for that window, so the turn cannot be double-sent. A turn
 * that could not be stored rejects instead, which restores the user's draft and
 * surfaces the reason.
 */
export function createComposerHandoffSubmission({
  onHandoff,
}: {
  onHandoff: (handoff: ComposerHandoff) => void
}): EstablishedAhpInitialSubmissionCoordinator {
  return {
    submit: async ({
      prompt,
      metadata,
      initialSelectedSpace,
    }: InitialAhpSubmissionRequest): Promise<EstablishedAhpSession> => {
      // Identifies this submission's turn among the dashboard pages sharing the
      // tab's storage key, so a page resumed from the back-forward cache can tell
      // its own record from one a newer submission has since put there.
      const id = crypto.randomUUID()
      const handoff: ComposerHandoff = {
        prompt,
        id,
        ...(metadata ? {metadata} : {}),
        ...(initialSelectedSpace ? {initialSelectedSpace} : {}),
      }

      // Yield before serializing so the composer can paint its submitting state
      // first. The write is synchronous and the payload can be a few hundred
      // kilobytes, so doing it inline would extend the Send interaction.
      await new Promise(resolve => setTimeout(resolve, 0))

      const failure = storeComposerHandoff(handoff)
      if (failure) throw new ComposerHandoffError(failure)

      try {
        onHandoff(handoff)
      } catch (error) {
        // The host could not take the turn on — it is what navigates to the
        // surface that would replay it — so the stored record has nobody coming
        // for it. Leaving it would hand a later Quick Chat visit a turn the user
        // was just told had failed, so it is withdrawn before the failure
        // propagates and the composer restores the draft.
        if (!clearComposerHandoff()) {
          // Except it could not be withdrawn, so it is still replayable and a
          // later visit will send it. Reporting a failure would restore a draft
          // the user could send again, duplicating that turn. The turn really is
          // out of this composer's hands, which is what an abandonment says.
          throw new SubmissionAbandonedError()
        }
        throw error
      }

      // Deliberately never resolves: the host navigates away in response, and
      // leaving the submission in flight keeps the composer locked for that
      // window so the turn cannot be sent twice.
      //
      // Except when the user comes back. A same-tab navigation can be restored
      // from the back-forward cache, which resumes React state rather than
      // remounting — so a promise that only ever stayed pending would leave this
      // composer locked for good. Settling on a persisted `pageshow` releases it.
      //
      // How it settles depends on whether the turn was claimed. The destination
      // claims it a macrotask after its own mount, so a quick Back — or a
      // destination that never got that far — can restore this page with the
      // record still in storage. Reporting that as an abandonment would clear the
      // draft as though the turn had been sent, while the record sat waiting to
      // be submitted by an unrelated Quick Chat visit later. So an unclaimed turn
      // is withdrawn here and reported as a failure, which puts the draft back.
      return new Promise<EstablishedAhpSession>((_resolve, reject) => {
        const onPageShow = (event: PageTransitionEvent) => {
          if (!event.persisted) return
          window.removeEventListener('pageshow', onPageShow)
          if (storedComposerHandoffId() !== id) {
            // Not this submission's record any more. Either the destination
            // claimed it, or a newer submission superseded it — which already
            // cleared this turn on its way in. Either way there is nothing here
            // to withdraw, and withdrawing what is there would delete a turn
            // another page is waiting on.
            reject(new SubmissionAbandonedError())
            return
          }
          if (!clearComposerHandoff()) {
            // Still stored, so a later visit will send it after all. Restoring
            // the draft would let the user send the same turn a second time.
            reject(new SubmissionAbandonedError())
            return
          }
          reject(new Error('The handed-off turn was withdrawn before it was claimed.'))
        }
        window.addEventListener('pageshow', onPageShow)
      })
    },
  }
}
